import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import {
  listPendingChanges,
  approve,
  reject,
  subscribe,
} from '../../services/approvalStore';
import { getUser } from '../../services/userDirectory';

/**
 * Approvals Page
 * ──────────────
 * Approver-only view of the pending change queue. Each card shows:
 *   - what's being proposed (action, target, patch)
 *   - who proposed it (with timestamp + reason)
 *   - approve / reject buttons (with optional review note)
 *
 * Self-approval is enforced at the store level, if the current user
 * proposed the change, the buttons are disabled and an explanatory note
 * is shown. This prevents the two-person rule from being circumvented.
 */
const ApprovalsPage = () => {
  const navigate = useNavigate();
  const [changes, setChanges] = useState(() => listPendingChanges());
  const [filter, setFilter] = useState('pending'); // pending | approved | rejected | all
  const [reviewNotes, setReviewNotes] = useState({}); // { [changeId]: string }
  const [errors, setErrors] = useState({}); // { [changeId]: errorMsg }

  useEffect(() => {
    return subscribe((all) => setChanges(all));
  }, []);

  // Read current user from session
  const currentUser = (() => {
    try { return JSON.parse(localStorage.getItem('user') || '{}'); }
    catch { return {}; }
  })();
  const currentUserId = currentUser?.email || null;
  const currentRole = currentUser?.role;

  const isApprover = currentRole === 'approver';

  const filtered = filter === 'all'
    ? changes
    : changes.filter((c) => c.status === filter);

  const handleApprove = (change) => {
    setErrors((e) => ({ ...e, [change.id]: null }));
    try {
      approve(change.id, currentUserId, reviewNotes[change.id] || '');
      // Apply the underlying mutation. For assessment status changes,
      // emit a custom event that the AssessmentDetailPanel listens for.
      window.dispatchEvent(new CustomEvent('clearequal.approvalCommitted', {
        detail: { change }
      }));
    } catch (err) {
      setErrors((e) => ({ ...e, [change.id]: err.message }));
    }
  };

  const handleReject = (change) => {
    setErrors((e) => ({ ...e, [change.id]: null }));
    try {
      reject(change.id, currentUserId, reviewNotes[change.id] || '');
    } catch (err) {
      setErrors((e) => ({ ...e, [change.id]: err.message }));
    }
  };

  const formatTime = (iso) => {
    try { return new Date(iso).toLocaleString('en-GB'); }
    catch { return iso; }
  };

  const actionLabels = {
    'assessment.changeStatus': 'Change assessment status',
    'assessment.close': 'Close joint assessment',
    'assessment.reassign': 'Reassign case',
    'payBand.publish': 'Publish pay band',
    'user.invite': 'Invite new user',
    'user.changeRole': 'Change user role',
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-20 pb-12 px-6 max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-semibold text-foreground">Approval Queue</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Review and ratify sensitive changes proposed by HR administrators.
              Two-person rule enforced, you cannot approve your own proposals.
            </p>
          </div>
          {!isApprover && (
            <span className="px-3 py-1 rounded-full text-xs bg-warning/10 text-warning font-medium">
              Read-only view, approver role required
            </span>
          )}
        </div>

        {/* Filter tabs */}
        <div className="flex gap-2 mb-6 border-b border-border">
          {[
            { id: 'pending', label: 'Pending', count: changes.filter((c) => c.status === 'pending').length },
            { id: 'approved', label: 'Approved', count: changes.filter((c) => c.status === 'approved').length },
            { id: 'rejected', label: 'Rejected', count: changes.filter((c) => c.status === 'rejected').length },
            { id: 'all', label: 'All', count: changes.length },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setFilter(tab.id)}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                filter === tab.id
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {tab.label}
              <span className="ml-2 text-xs opacity-70">{tab.count}</span>
            </button>
          ))}
        </div>

        {/* Empty state */}
        {filtered.length === 0 && (
          <div className="text-center py-16 border border-dashed border-border rounded-lg">
            <Icon name="CheckCircle2" size={32} className="text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground">No {filter !== 'all' ? filter : ''} changes to review.</p>
          </div>
        )}

        {/* Change cards */}
        <div className="space-y-4">
          {filtered.map((change) => {
            const proposer = getUser(change.proposedBy);
            const isSelfProposed = change.proposedBy === currentUserId;
            const isPending = change.status === 'pending';

            return (
              <div
                key={change.id}
                className="bg-card border border-border rounded-lg p-5"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-foreground">
                        {actionLabels[change.action] || change.action}
                      </h3>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        change.status === 'pending' ? 'bg-warning/10 text-warning' :
                        change.status === 'approved' ? 'bg-success/10 text-success' :
                        change.status === 'rejected' ? 'bg-destructive/10 text-destructive' :
                        'bg-muted text-muted-foreground'
                      }`}>
                        {change.status}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Target: <span className="font-mono">{change.targetType}/{change.targetId}</span>
                    </p>
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {formatTime(change.proposedAt)}
                  </span>
                </div>

                {/* Patch details */}
                <div className="bg-muted/50 rounded p-3 mb-3 text-sm">
                  <p className="text-xs text-muted-foreground mb-1 uppercase tracking-wide">Proposed change</p>
                  {Object.entries(change.patch || {}).map(([k, v]) => (
                    <div key={k} className="font-mono text-xs">
                      {k}: <span className="text-primary">{JSON.stringify(v)}</span>
                    </div>
                  ))}
                </div>

                {/* Proposer info */}
                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
                  <Icon name="User" size={14} />
                  Proposed by <span className="text-foreground font-medium">{proposer?.name || change.proposedBy}</span>
                  {change.reason && (
                    <span className="italic">, "{change.reason}"</span>
                  )}
                </div>

                {/* Already-reviewed metadata */}
                {!isPending && change.reviewedBy && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
                    <Icon name="ShieldCheck" size={14} />
                    {change.status === 'approved' ? 'Approved' : 'Rejected'} by{' '}
                    <span className="text-foreground font-medium">
                      {getUser(change.reviewedBy)?.name || change.reviewedBy}
                    </span>{' '}
                    on {formatTime(change.reviewedAt)}
                    {change.reviewNote && <span className="italic">, "{change.reviewNote}"</span>}
                  </div>
                )}

                {/* Self-proposal warning */}
                {isPending && isSelfProposed && (
                  <div className="bg-warning/10 border border-warning/30 rounded p-3 text-sm text-warning mb-3">
                    <Icon name="AlertTriangle" size={14} className="inline mr-1" />
                    You proposed this change. A different approver must review it (two-person rule).
                  </div>
                )}

                {/* Action buttons */}
                {isPending && isApprover && !isSelfProposed && (
                  <div className="space-y-2 pt-3 border-t border-border">
                    <textarea
                      placeholder="Review note (optional)..."
                      value={reviewNotes[change.id] || ''}
                      onChange={(e) =>
                        setReviewNotes((n) => ({ ...n, [change.id]: e.target.value }))
                      }
                      rows={2}
                      className="w-full px-3 py-2 text-sm bg-background border border-border rounded-md focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                    {errors[change.id] && (
                      <p className="text-sm text-destructive">{errors[change.id]}</p>
                    )}
                    <div className="flex gap-2">
                      <Button
                        variant="default"
                        size="sm"
                        iconName="Check"
                        onClick={() => handleApprove(change)}
                      >
                        Approve & Apply
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        iconName="X"
                        onClick={() => handleReject(change)}
                      >
                        Reject
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </main>
    </div>
  );
};

export default ApprovalsPage;
